/**
 * Inbox data + controller hooks (PHASE1-BUILD-SPEC §7.2; groups: "Serien" plan).
 *
 * `useInbox` is the raw infinite keyset-cursor query over `GET /api/inbox`.
 * It is session-stable (`staleTime: Infinity`, no focus refetch): the queue and
 * the user's place must never shift mid-triage, and the optimistic bookkeeping
 * below (`resolvedIds` vs `meta.remaining`) is only sound while the cached
 * pages stay put. `useInboxGroups` is a sibling raw query over the read-only,
 * nothing-persisted `GET /api/inbox/groups` (detected same-payee/PSP-sub-key
 * "Serien"), fetched once per session on the same terms.
 *
 * `useInboxController` composes both into the categorization workflow: it
 * advances the queue optimistically (a local `resolvedIds` Set so `current`
 * recomputes to the next item on the same render), keeps a LIFO `undoStack` of
 * `{kind:'single', id}` and `{kind:'group', key, ids}` entries, prefetches more
 * items when fewer than five remain unresolved, and drives the
 * transaction-scoped `categorize`/`skip`/`reopen` endpoints — plus, when the
 * current item belongs to a detected group and the group checkbox is enabled,
 * the `batch-categorize`/`batch-reopen` endpoints for every still-pending
 * member at once (`advanceGroup`). A group is "applied" by default only when
 * its `confidence` is `'high'`; an explicit user toggle (`groupChecked`,
 * session-only) overrides that default. Groups the user dismisses via
 * "Ignorieren" are recorded in the session-only `ignoredGroups` Set (never
 * shown again this session) — the same pattern `TransfersPage` uses for
 * ignored candidate pairs.
 *
 * Invalidation policy (§7.3): every settled mutation — single or batch —
 * invalidates `['transactions']` (+ reports/budgets) only — the `['inbox']`
 * cache (which `qk.inboxGroups` lives under) is deliberately left stable for
 * the whole session and invalidated exactly once when the controller unmounts.
 * Should the pages refetch anyway (manual retry), the derived views ignore
 * bookkeeping entries whose items left the cached pages: `remaining` subtracts
 * only resolved ids still in the queue (the refetched `meta.remaining` already
 * excludes the rest) plus a separate `offQueueResolved` count for
 * batch-resolved ids that are NOT (yet, or ever) in the cached pages — a group
 * can span ids on pages that were never fetched this session, and those would
 * otherwise never stop being counted as "remaining". The undo stack skips
 * entries whose item(s) could never re-surface this session (a `'single'`
 * entry needs its id in the queue; a `'group'` entry is live if ANY member id
 * is). A group's undo entry is trimmed, on the batch's success, to the
 * server's `categorized` ids only — the server may skip a member (already
 * categorized concurrently, deleted, transfer-linked, …), and undo must never
 * reopen an id it never actually touched; skipped ids stay resolved locally
 * since they are no longer pending server-side regardless.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api, ApiRequestError, type ApiResponse } from '../api.ts';
import { qk } from '../queryKeys.ts';
import { useBuckets } from './useBuckets.ts';
import { useToast } from '../../context/ToastContext.tsx';
import type {
  BatchCategorizeIn,
  BatchCategorizeOut,
  BatchCategorizeSource,
  BatchReopenIn,
  BatchReopenOut,
  BucketOut,
  CategorizeIn,
  InboxGroup,
  InboxItem,
  InboxMeta,
  SplitInput,
  TransactionDetailOut,
} from '../types.ts';

/** Ordering of the inbox queue; the server default is newest-booking-first. */
export type InboxOrder = 'newest';

const INBOX_PAGE_SIZE = 20;
/** Prefetch the next page while fewer than this many items remain unresolved. */
const PREFETCH_THRESHOLD = 5;

type InboxPage = ApiResponse<InboxItem[], InboxMeta>;

/** Raw infinite inbox query — keyed off `['inbox']` so a prefix invalidation hits it. */
export function useInbox(
  order: InboxOrder = 'newest',
): UseInfiniteQueryResult<InfiniteData<InboxPage, string | undefined>, ApiRequestError> {
  return useInfiniteQuery<
    InboxPage,
    ApiRequestError,
    InfiniteData<InboxPage, string | undefined>,
    readonly [...typeof qk.inbox, InboxOrder],
    string | undefined
  >({
    queryKey: [...qk.inbox, order],
    initialPageParam: undefined,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({
        limit: String(INBOX_PAGE_SIZE),
        order,
      });
      if (pageParam) params.set('cursor', pageParam);
      return api.getEnvelope<InboxItem[], InboxMeta>(`/api/inbox?${params.toString()}`);
    },
    getNextPageParam: (lastPage) => lastPage.meta?.next_cursor ?? undefined,
    // Session-stable (see module docstring): a focus/staleness refetch mid-
    // triage would drop optimistically-resolved items from the pages while the
    // local bookkeeping still counts them — the queue must not shift under the
    // user. The controller invalidates once on unmount instead.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** Raw detected-groups query ("Serien") — nothing persisted, recomputed on
 * read, fetched once per inbox session like `useInbox`. */
export function useInboxGroups(): UseQueryResult<InboxGroup[], ApiRequestError> {
  return useQuery<InboxGroup[], ApiRequestError>({
    queryKey: qk.inboxGroups,
    queryFn: () => api.get<InboxGroup[]>('/api/inbox/groups'),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** PATCH the Notiz of an inbox item. Deliberately does NOT invalidate
 * `['inbox']` — the queue is session-stable (see module docstring) and a
 * refetch mid-triage would shift it under the user. The cached pages are
 * patched in place instead, so the note shows immediately on the card. */
export function useSaveInboxNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: number; notes: string | null }) =>
      api.patch<TransactionDetailOut>(`/api/transactions/${id}`, { notes }),
    onSuccess: (_result, { id, notes }) => {
      // The `['inbox']` prefix also matches `qk.inboxGroups` (an array, no
      // `pages`) — the guard leaves any non-infinite entry untouched.
      queryClient.setQueriesData<InfiniteData<InboxPage, string | undefined>>(
        { queryKey: qk.inbox },
        (data) =>
          data && 'pages' in data
            ? {
                ...data,
                pages: data.pages.map((page) => ({
                  ...page,
                  data: page.data.map((item) => (item.id === id ? { ...item, notes } : item)),
                })),
              }
            : data,
      );
      void queryClient.invalidateQueries({ queryKey: qk.transactions });
    },
  });
}

/** The last resolved action, used to (re)surface the 5-second undo affordance. */
export interface InboxLastAction {
  seq: number;
  label: string;
}

/** A LIFO undo-stack entry: one resolved transaction, or one group batch
 * action covering several. */
export type UndoEntry = { kind: 'single'; id: number } | { kind: 'group'; key: string; ids: number[] };

export interface InboxController {
  /** The transaction currently awaiting a decision, or `null` when the queue is empty. */
  current: InboxItem | null;
  /** Remaining pending count (server total minus optimistically resolved). */
  remaining: number;
  isLoading: boolean;
  isError: boolean;
  error: ApiRequestError | null;
  refetch: () => void;
  /** Canonical bucket order (group then bucket sort_order) — `1–9` maps to `[n-1]`. */
  gridBuckets: BucketOut[];
  /** member id → the detected group it belongs to (session-recomputed). */
  groupByTxn: Map<number, InboxGroup>;
  /** The group covering `current`, or `null` (no group, or the user ignored it). */
  currentGroup: InboxGroup | null;
  /** Whether `assign`/`acceptSuggestion` apply to the whole `currentGroup`.
   * An explicit `setGroupApplyEnabled` call overrides the confidence default
   * (checked on by default only for `confidence === 'high'`). */
  groupApplyEnabled: boolean;
  setGroupApplyEnabled: (enabled: boolean) => void;
  /** Session-only: hide a group's banner for the rest of the session (`TransfersPage` pattern). */
  ignoreGroup: (key: string) => void;
  /** A group's member ids not yet optimistically resolved. */
  liveMemberIds: (group: InboxGroup) => number[];
  /** The bucket id of the most recent group batch action this session, if any
   * — the fallback bucket for "Als Regel speichern" when the group carries no
   * suggestion of its own. */
  lastGroupBucketId: number | null;
  /** Categorize `current` into one full-amount split (`categorized_by='user'`).
   * Batches the whole `currentGroup` instead when it is set and applied. */
  assign: (bucketId: number) => void;
  /** Accept `current.suggestion` as a full-amount split (`suggestion_accepted`).
   * Batches the whole `currentGroup` instead when it is set and applied. Only
   * the current card's own (visible) suggestion is ever accepted — the group's
   * aggregate suggestion merely prefills "Als Regel speichern". */
  acceptSuggestion: () => void;
  /** Categorize `current` with explicit split lines from the SplitEditor —
   * always single-transaction, regardless of any active group. */
  split: (splits: SplitInput[]) => void;
  /** Skip `current` (`review_status='skipped'`) — always single-transaction. */
  skip: () => void;
  /** LIFO undo — reopens the most recently resolved item or group. */
  undo: () => void;
  canUndo: boolean;
  lastAction: InboxLastAction | null;
}

function errorMessage(err: unknown): string {
  return err instanceof ApiRequestError ? err.message : 'Aktion fehlgeschlagen.';
}

function lastIndexOfSingle(stack: readonly UndoEntry[], id: number): number {
  for (let i = stack.length - 1; i >= 0; i -= 1) {
    const entry = stack[i];
    if (entry.kind === 'single' && entry.id === id) return i;
  }
  return -1;
}

function lastIndexOfGroup(stack: readonly UndoEntry[], key: string): number {
  for (let i = stack.length - 1; i >= 0; i -= 1) {
    const entry = stack[i];
    if (entry.kind === 'group' && entry.key === key) return i;
  }
  return -1;
}

export function useInboxController(order: InboxOrder = 'newest'): InboxController {
  const query = useInbox(order);
  const groupsQuery = useInboxGroups();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const gridBuckets = useBuckets();

  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  // Local optimistic state: items already acted upon, and the LIFO undo stack.
  const [resolvedIds, setResolvedIds] = useState<ReadonlySet<number>>(() => new Set());
  // Batch-resolved ids, tracked separately so `remaining` can subtract the
  // ones not (yet) present in a fetched page — see module docstring.
  const [offQueueResolved, setOffQueueResolved] = useState<ReadonlySet<number>>(() => new Set());
  const [undoStack, setUndoStack] = useState<readonly UndoEntry[]>([]);
  const [lastAction, setLastAction] = useState<InboxLastAction | null>(null);
  const seqRef = useRef(0);
  const undoStackRef = useRef<readonly UndoEntry[]>(undoStack);
  useEffect(() => {
    undoStackRef.current = undoStack;
  }, [undoStack]);

  // Session-only group bookkeeping (TransfersPage hidden-Set pattern).
  const [ignoredGroups, setIgnoredGroups] = useState<ReadonlySet<string>>(() => new Set());
  const [groupChecked, setGroupChecked] = useState<Readonly<Record<string, boolean>>>({});
  const [lastGroupBucketId, setLastGroupBucketId] = useState<number | null>(null);

  const queue = useMemo<InboxItem[]>(
    () => (data?.pages ?? []).flatMap((page) => page.data),
    [data],
  );
  const queueIds = useMemo(() => new Set(queue.map((item) => item.id)), [queue]);
  const queueIdsRef = useRef(queueIds);
  useEffect(() => {
    queueIdsRef.current = queueIds;
  }, [queueIds]);
  const unresolved = useMemo(
    () => queue.filter((item) => !resolvedIds.has(item.id)),
    [queue, resolvedIds],
  );
  const current = unresolved[0] ?? null;

  const groups = groupsQuery.data;
  const groupByTxn = useMemo(() => {
    const map = new Map<number, InboxGroup>();
    for (const group of groups ?? []) {
      for (const id of group.member_ids) map.set(id, group);
    }
    return map;
  }, [groups]);

  const currentGroup = useMemo(() => {
    if (!current) return null;
    const group = groupByTxn.get(current.id);
    if (!group) return null;
    return ignoredGroups.has(group.key) ? null : group;
  }, [current, groupByTxn, ignoredGroups]);

  const groupApplyEnabled = currentGroup
    ? (groupChecked[currentGroup.key] ?? currentGroup.confidence === 'high')
    : false;

  const setGroupApplyEnabled = useCallback(
    (enabled: boolean) => {
      const group = currentGroup;
      if (!group) return;
      setGroupChecked((prev) => ({ ...prev, [group.key]: enabled }));
    },
    [currentGroup],
  );

  const ignoreGroup = useCallback((key: string) => {
    setIgnoredGroups((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  const liveMemberIds = useCallback(
    (group: InboxGroup) => group.member_ids.filter((id) => !resolvedIds.has(id)),
    [resolvedIds],
  );

  // Undoable entries: only those whose item(s) are still present in the cached
  // pages. After a refetch, resolved items vanish from the pages — reopening
  // them would succeed server-side but the item could never re-surface this
  // session, so such stale stack entries are skipped rather than offered. A
  // group entry is live if ANY of its ids is still in the cached pages.
  const liveUndoStack = useMemo(
    () =>
      undoStack.filter((entry) =>
        entry.kind === 'single' ? queueIds.has(entry.id) : entry.ids.some((id) => queueIds.has(id)),
      ),
    [undoStack, queueIds],
  );

  const serverRemaining = data?.pages[0]?.meta?.remaining ?? 0;
  // Subtract only resolved items STILL present in the cached pages — a
  // refetched `meta.remaining` (or partner-side concurrency) already excludes
  // the rest; subtracting the full Set would double-count them.
  const resolvedInQueue = useMemo(() => {
    let count = 0;
    for (const item of queue) if (resolvedIds.has(item.id)) count += 1;
    return count;
  }, [queue, resolvedIds]);
  // Batch-resolved ids NOT in the queue: a group can span members on pages
  // never fetched this session, so they never appear in `queue`/`resolvedInQueue`
  // above. Computed live against `queueIds` so a member that later appears on a
  // fetched page (a keyset page load) stops being double-tracked here — it is
  // then covered by `resolvedInQueue` instead.
  const offQueueResolvedCount = useMemo(() => {
    let count = 0;
    for (const id of offQueueResolved) if (!queueIds.has(id)) count += 1;
    return count;
  }, [offQueueResolved, queueIds]);
  const remaining = Math.max(0, serverRemaining - resolvedInQueue - offQueueResolvedCount);

  // Prefetch: keep at least a handful of items ahead of the user.
  useEffect(() => {
    if (unresolved.length < PREFETCH_THRESHOLD && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [unresolved.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Invalidate the inbox cache exactly once, on unmount (leaving the tab).
  // `qk.inboxGroups` lives under the `['inbox']` prefix, so this refreshes the
  // detected groups too.
  useEffect(() => {
    return () => {
      void queryClient.invalidateQueries({ queryKey: qk.inbox });
    };
  }, [queryClient]);

  // In-flight resolve (categorize/skip/batch) request per transaction id.
  // `undo()` sequences its `/reopen` (or `/batch-reopen`) behind this promise:
  // fired concurrently, the two POSTs can commit in either order server-side,
  // and a reopen that lands BEFORE the categorize is silently overwritten by
  // it (the undo is lost). A group batch stores the SAME promise under every
  // member id.
  const inflightRef = useRef(new Map<number, Promise<unknown>>());

  /**
   * Optimistically resolve `item`: mark it resolved (so `current` advances this
   * render), push it onto the undo stack, then fire `run`. On failure the
   * optimistic state is rolled back and an error toast is shown; either way the
   * `['transactions']` cache is invalidated once the request settles.
   */
  const advance = useCallback(
    (item: InboxItem, label: string, run: (id: number) => Promise<unknown>) => {
      setResolvedIds((prev) => {
        if (prev.has(item.id)) return prev;
        const next = new Set(prev);
        next.add(item.id);
        return next;
      });
      setUndoStack((prev) => [...prev, { kind: 'single', id: item.id }]);
      seqRef.current += 1;
      setLastAction({ seq: seqRef.current, label });

      const request = run(item.id);
      inflightRef.current.set(item.id, request);
      request
        .catch((err: unknown) => {
          setResolvedIds((prev) => {
            if (!prev.has(item.id)) return prev;
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
          setUndoStack((prev) => {
            const idx = lastIndexOfSingle(prev, item.id);
            if (idx === -1) return prev;
            const copy = [...prev];
            copy.splice(idx, 1);
            return copy;
          });
          toast(errorMessage(err), { variant: 'error' });
        })
        .finally(() => {
          if (inflightRef.current.get(item.id) === request) {
            inflightRef.current.delete(item.id);
          }
          void queryClient.invalidateQueries({ queryKey: qk.transactions });
          void queryClient.invalidateQueries({ queryKey: qk.reports });
          void queryClient.invalidateQueries({ queryKey: qk.budgets });
        });
    },
    [queryClient, toast],
  );

  /**
   * Optimistically resolve every id in `ids` (a group's live members) as one
   * unit: all ids join `resolvedIds`/`offQueueResolved` in a single render,
   * one group undo entry is pushed, and the SAME `run(ids)` promise is shared
   * across every member in `inflightRef`. On success the undo entry is trimmed
   * to `response.categorized` (never reopen a member the server skipped —
   * skipped ids simply stay resolved, they are no longer pending server-side
   * regardless). On failure every id is rolled back and the entry is dropped.
   */
  const advanceGroup = useCallback(
    (group: InboxGroup, ids: number[], label: string, run: (ids: number[]) => Promise<BatchCategorizeOut>) => {
      if (ids.length === 0) return;
      setResolvedIds((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.add(id);
        return next;
      });
      setOffQueueResolved((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.add(id);
        return next;
      });
      setUndoStack((prev) => [...prev, { kind: 'group', key: group.key, ids: [...ids] }]);
      seqRef.current += 1;
      setLastAction({ seq: seqRef.current, label });

      const request = run(ids);
      for (const id of ids) inflightRef.current.set(id, request);

      request
        .then((result) => {
          const categorizedSet = new Set(result.categorized);
          setUndoStack((prev) => {
            const idx = lastIndexOfGroup(prev, group.key);
            if (idx === -1) return prev;
            const entry = prev[idx];
            if (entry.kind !== 'group') return prev;
            const trimmed = entry.ids.filter((id) => categorizedSet.has(id));
            const copy = [...prev];
            if (trimmed.length === 0) {
              copy.splice(idx, 1);
            } else {
              copy[idx] = { ...entry, ids: trimmed };
            }
            return copy;
          });
        })
        .catch((err: unknown) => {
          setResolvedIds((prev) => {
            const next = new Set(prev);
            for (const id of ids) next.delete(id);
            return next;
          });
          setOffQueueResolved((prev) => {
            const next = new Set(prev);
            for (const id of ids) next.delete(id);
            return next;
          });
          setUndoStack((prev) => {
            const idx = lastIndexOfGroup(prev, group.key);
            if (idx === -1) return prev;
            const copy = [...prev];
            copy.splice(idx, 1);
            return copy;
          });
          toast(errorMessage(err), { variant: 'error' });
        })
        .finally(() => {
          for (const id of ids) {
            if (inflightRef.current.get(id) === request) {
              inflightRef.current.delete(id);
            }
          }
          void queryClient.invalidateQueries({ queryKey: qk.transactions });
          void queryClient.invalidateQueries({ queryKey: qk.reports });
          void queryClient.invalidateQueries({ queryKey: qk.budgets });
        });
    },
    [queryClient, toast],
  );

  const categorize = useCallback(
    (id: number, body: CategorizeIn) => api.post(`/api/transactions/${id}/categorize`, body),
    [],
  );

  const batchCategorize = useCallback((ids: number[], bucketId: number, source: BatchCategorizeSource) => {
    const body: BatchCategorizeIn = { transaction_ids: ids, bucket_id: bucketId, source };
    return api.post<BatchCategorizeOut>('/api/transactions/batch-categorize', body);
  }, []);

  const assign = useCallback(
    (bucketId: number) => {
      const item = current;
      if (!item) return;
      if (currentGroup && groupApplyEnabled) {
        const ids = liveMemberIds(currentGroup);
        if (ids.length === 0) return;
        setLastGroupBucketId(bucketId);
        advanceGroup(currentGroup, ids, `Serie kategorisiert (${ids.length})`, () =>
          batchCategorize(ids, bucketId, 'grid'),
        );
        return;
      }
      advance(item, 'Kategorisiert', (id) =>
        categorize(id, {
          splits: [{ bucket_id: bucketId, amount: item.amount }],
          source: 'grid',
        }),
      );
    },
    [current, currentGroup, groupApplyEnabled, liveMemberIds, advanceGroup, batchCategorize, advance, categorize],
  );

  const acceptSuggestion = useCallback(() => {
    const item = current;
    // Only the VISIBLE suggestion (the current card's own banner) is ever
    // accepted — applying the group's aggregate suggestion here would
    // categorize N rows into a bucket the user never saw on screen.
    if (!item?.suggestion) return;
    const bucketId = item.suggestion.bucket_id;
    if (currentGroup && groupApplyEnabled) {
      const ids = liveMemberIds(currentGroup);
      if (ids.length === 0) return;
      setLastGroupBucketId(bucketId);
      advanceGroup(currentGroup, ids, `Serie kategorisiert (${ids.length})`, () =>
        batchCategorize(ids, bucketId, 'accept'),
      );
      return;
    }
    advance(item, 'Vorschlag übernommen', (id) =>
      categorize(id, {
        splits: [{ bucket_id: bucketId, amount: item.amount }],
        source: 'accept',
      }),
    );
  }, [current, currentGroup, groupApplyEnabled, liveMemberIds, advanceGroup, batchCategorize, advance, categorize]);

  const split = useCallback(
    (splits: SplitInput[]) => {
      const item = current;
      if (!item) return;
      advance(item, 'Aufgeteilt', (id) => categorize(id, { splits, source: 'split' }));
    },
    [current, advance, categorize],
  );

  const skip = useCallback(() => {
    const item = current;
    if (!item) return;
    advance(item, 'Übersprungen', (id) => api.post(`/api/transactions/${id}/skip`));
  }, [current, advance]);

  const undo = useCallback(() => {
    // Consider only entries whose item(s) are still in the cached pages (see
    // `liveUndoStack`) — stale ids cannot re-surface and must not be reopened.
    const stack = undoStackRef.current.filter((entry) =>
      entry.kind === 'single'
        ? queueIdsRef.current.has(entry.id)
        : entry.ids.some((id) => queueIdsRef.current.has(id)),
    );
    if (stack.length === 0) return;
    const entry = stack[stack.length - 1];

    setUndoStack((prev) => {
      const idx =
        entry.kind === 'single' ? lastIndexOfSingle(prev, entry.id) : lastIndexOfGroup(prev, entry.key);
      if (idx === -1) return prev;
      const copy = [...prev];
      copy.splice(idx, 1);
      return copy;
    });

    const settleInvalidate = () => {
      void queryClient.invalidateQueries({ queryKey: qk.transactions });
      void queryClient.invalidateQueries({ queryKey: qk.reports });
      void queryClient.invalidateQueries({ queryKey: qk.budgets });
    };

    if (entry.kind === 'single') {
      const id = entry.id;
      const reopen = () => {
        api
          .post(`/api/transactions/${id}/reopen`)
          .then(() => {
            setResolvedIds((prev) => {
              if (!prev.has(id)) return prev;
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
          })
          .catch((err: unknown) => {
            setUndoStack((prev) => [...prev, entry]);
            toast(errorMessage(err), { variant: 'error' });
          })
          .finally(settleInvalidate);
      };
      const pending = inflightRef.current.get(id);
      if (pending) {
        // Sequence the reopen BEHIND the still-in-flight categorize/skip so the
        // server commits them in the user's order. If that request failed,
        // advance()'s rollback already restored the item — skip the reopen.
        void pending.then(reopen, () => undefined);
      } else {
        reopen();
      }
    } else {
      const ids = entry.ids;
      const reopenGroup = () => {
        const body: BatchReopenIn = { transaction_ids: ids };
        api
          .post<BatchReopenOut>('/api/transactions/batch-reopen', body)
          .then((result) => {
            const reopenedSet = new Set(result.reopened);
            setResolvedIds((prev) => {
              const next = new Set(prev);
              for (const id of ids) if (reopenedSet.has(id)) next.delete(id);
              return next;
            });
            setOffQueueResolved((prev) => {
              const next = new Set(prev);
              for (const id of ids) if (reopenedSet.has(id)) next.delete(id);
              return next;
            });
          })
          .catch((err: unknown) => {
            setUndoStack((prev) => [...prev, entry]);
            toast(errorMessage(err), { variant: 'error' });
          })
          .finally(settleInvalidate);
      };
      // All of a group's ids share the SAME batch promise (see `advanceGroup`)
      // — the first id's in-flight entry represents the whole batch.
      const pending = inflightRef.current.get(ids[0]);
      if (pending) {
        void pending.then(reopenGroup, () => undefined);
      } else {
        reopenGroup();
      }
    }
  }, [queryClient, toast]);

  return {
    current,
    remaining,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ?? null,
    refetch: () => void query.refetch(),
    gridBuckets,
    groupByTxn,
    currentGroup,
    groupApplyEnabled,
    setGroupApplyEnabled,
    ignoreGroup,
    liveMemberIds,
    lastGroupBucketId,
    assign,
    acceptSuggestion,
    split,
    skip,
    undo,
    canUndo: liveUndoStack.length > 0,
    lastAction,
  };
}
